import type { ReactElement } from 'react';
import { useEffect } from 'react';
import DocumentHeader from '@components/DocumentHeader';
import { Heading, DetailedError, Copy } from './styled';
import { Row } from '@simplywallst/ui-core';
import { Container } from './styled';
import { RouterLink } from '@redux-first-router-link';
import {
  ROUTE_DEPRECATED_PORTFOLIO,
  ROUTE_DEPRECATED_PORTFOLIO_HOME,
  ROUTE_STOCKS_HOME,
  ROUTE_DISCOVER_INVESTING_IDEAS,
  ROUTE_DASHBOARD,
} from '@/constants/routes';
import { SUPPORT_EMAIL } from '@/constants';
import { useSelector, shallowEqual } from 'react-redux';
import Logo from '@assets/images/logo/warren-ice-cream.svg';
import type { State as PageState } from '@/reducers/page';
import type { State as PortfoliosState } from '@/components/DeprecatedPortfolios/redux/reducers';
import type { PortfolioState } from '@/components/DeprecatedPortfolio/redux/interface';
import type { LocationState } from 'redux-first-router';
import If from '@components/If';
import type { SwsTeam } from '@components/Sentry';
import { sentryCaptureMessage } from '@components/Sentry';
import GlobalStyle from '@/styled/styled.default';

export interface Props {
  customCopy?: ReactElement;
  globalDisabled?: boolean;
  isShownLogo?: boolean;
  isShownHeading?: boolean;
  resetError?: () => void;
  team?: SwsTeam;
}

enum ErrorType {
  GENERIC,
  PORTFOLIO_LINK,
  OTHER_PORTFOLIO_LINK,
}

const Oops = ({
  customCopy,
  globalDisabled,
  resetError,
  isShownLogo = true,
  isShownHeading = true,
  team,
}: Props) => {
  const errorMessage = useSelector<
    {
      page: PageState;
      portfolio: PortfolioState;
      portfolios: PortfoliosState;
      location: LocationState;
    },
    { message: string; type: ErrorType }
  >((state) => {
    if (typeof state.page.exception !== 'undefined') {
      const { portfolio, portfolios, location } = state;
      if (
        [ROUTE_DEPRECATED_PORTFOLIO, ROUTE_DEPRECATED_PORTFOLIO_HOME].includes(
          location.type
        )
      ) {
        const { exception } = state.page;
        switch (exception.error) {
          case 'PortfolioLinkExpiredException':
            let currentPortfolioId = '';
            if (
              location.type === ROUTE_DEPRECATED_PORTFOLIO_HOME &&
              portfolio.currentId !== null
            ) {
              // need to select portfolio id
              currentPortfolioId = portfolio.currentId;
            } else {
              currentPortfolioId = location.payload.portfolioId;
              // can use payload.portfolioId
            }
            if (currentPortfolioId in portfolios.entities) {
              const { name, provider } =
                portfolios.entities[currentPortfolioId];
              return {
                type: ErrorType.PORTFOLIO_LINK,
                message: `Your ${provider} access token has expired and we had to remove your portfolio (${name}).`,
              };
            }
            return {
              type: ErrorType.PORTFOLIO_LINK,
              message: `Your provider access token has expired and we had to remove your porfolio.`,
            };
          default:
            return {
              type: ErrorType.OTHER_PORTFOLIO_LINK,
              message: exception.reason,
            };
        }
      }
      return {
        type: ErrorType.GENERIC,
        message: `We're sorry you ran into a bug!`,
      };
    }
    return {
      type: ErrorType.GENERIC,
      message: `We're sorry you ran into a bug!`,
    };
  }, shallowEqual);

  useEffect(() => {
    if (!globalDisabled) {
      sentryCaptureMessage({
        team,
        message: `Global crashed - ${errorMessage.type} - ${errorMessage.message}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Row
      height={globalDisabled ? 'auto' : '100vh'}
      width={globalDisabled ? 'auto' : '100vw'}
      data-cy-id={globalDisabled ? 'component-error' : 'page-error'}
    >
      {!globalDisabled && <GlobalStyle />}
      {!globalDisabled && (
        <DocumentHeader title={'An error has occured'} titleSuffix="" />
      )}
      <Container
        globalDisabled={globalDisabled}
        justifyContent="center"
        alignItem="center"
        alignContent="center"
        displayFlex
        column
      >
        {isShownLogo && <Logo />}
        {isShownHeading && (
          <If
            conditions={{
              generic: errorMessage.type === ErrorType.GENERIC,
              portfolioLink: errorMessage.type === ErrorType.PORTFOLIO_LINK,
              otherPortfolioLink:
                errorMessage.type === ErrorType.OTHER_PORTFOLIO_LINK,
            }}
          >
            {{
              generic: <Heading>{errorMessage.message}</Heading>,
              portfolioLink: (
                <>
                  <DetailedError>
                    {`We're really sorry about this. ${errorMessage.message}`}
                  </DetailedError>
                </>
              ),
              otherPortfolioLink: (
                <>
                  <Heading>
                    Something went wrong while trying to load your portfolio.
                  </Heading>
                  <DetailedError>{errorMessage.message}</DetailedError>
                  <Copy>
                    <span>Here are other useful links:</span>
                    <ul>
                      <li>
                        <RouterLink to={{ type: ROUTE_DEPRECATED_PORTFOLIO }}>
                          My portfolios
                        </RouterLink>
                      </li>
                      <li>
                        <RouterLink to={{ type: ROUTE_STOCKS_HOME }}>
                          Browse for companies
                        </RouterLink>
                      </li>
                      <li>
                        <RouterLink
                          to={{ type: ROUTE_DISCOVER_INVESTING_IDEAS }}
                        >
                          Discover investing ideas
                        </RouterLink>
                      </li>
                    </ul>
                  </Copy>
                </>
              ),
            }}
          </If>
        )}
        {customCopy ? (
          customCopy
        ) : (
          <Copy>
            <span>Return to </span>
            <RouterLink to={{ type: ROUTE_DASHBOARD }} onClick={resetError}>
              Dashboard
            </RouterLink>
            <span> or </span>
            <a href={SUPPORT_EMAIL} rel="nofollow">
              send us an email
            </a>
            <span>{` to let us know what happened so we can fix the issue.`}</span>
          </Copy>
        )}
      </Container>
    </Row>
  );
};

export default Oops;
